import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, LogOut, Pencil, Home, Wrench } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBar() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    document.body.classList.add("admin-bar-on");
    return () => document.body.classList.remove("admin-bar-on");
  }, [user]);

  if (!user) return null;

  const logout = async () => {
    await axios
      .post(`${API}/auth/logout`, {}, { withCredentials: true })
      .catch(() => {});
    setUser(false);
    toast.success("Session terminée");
  };

  return (
    <header
      data-testid="admin-bar"
      className="fixed inset-x-0 top-0 z-[80] border-b border-[#F4F1EB]/15 bg-[#102C1F] text-[#F4F1EB]"
    >
      <div className="mx-auto flex h-10 max-w-7xl items-center justify-between px-5 md:px-10">
        <span className="font-display text-sm italic">
          J'aime la vie — Gestion
        </span>
        <div className="flex items-center gap-2">
          <details data-testid="admin-bar-menu" className="group relative">
            <summary
              data-testid="admin-bar-menu-btn"
              className="flex cursor-pointer list-none items-center gap-1.5 rounded-full border border-[#F4F1EB]/30 px-3 py-1 text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#F4F1EB] hover:text-[#102C1F] [&::-webkit-details-marker]:hidden"
            >
              Menu
              <ChevronDown
                size={12}
                className="transition-transform duration-300 group-open:rotate-180"
              />
            </summary>
            <div className="absolute right-0 mt-2 min-w-52 rounded-sm border border-[#102C1F]/15 bg-[#F4F1EB] py-1.5 text-[#102C1F] shadow-[0_20px_40px_-20px_rgba(16,44,31,0.4)]">
              <Link
                data-testid="admin-bar-home-link"
                to="/"
                onClick={(e) =>
                  (e.currentTarget.closest("details").open = false)
                }
                className="flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#102C1F]/5"
              >
                <Home size={11} /> Accueil
              </Link>
              <Link
                data-testid="admin-bar-gestion-link"
                to="/admin"
                onClick={(e) =>
                  (e.currentTarget.closest("details").open = false)
                }
                className="flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#102C1F]/5"
              >
                <Wrench size={11} /> Gestion
              </Link>
              <details data-testid="admin-bar-editor-menu" className="group/ed">
                <summary
                  data-testid="admin-bar-editor-btn"
                  className="flex cursor-pointer list-none items-center justify-between px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#102C1F]/5 [&::-webkit-details-marker]:hidden"
                >
                  <span className="flex items-center gap-2">
                    <Pencil size={11} /> Éditeur
                  </span>
                  <ChevronDown
                    size={12}
                    className="transition-transform duration-300 group-open/ed:rotate-180"
                  />
                </summary>
                <div className="border-y border-[#102C1F]/10 bg-[#102C1F]/[0.03]">
                  {[
                    ["admin-bar-edit-link", "/admin#plat-du-jour", "Plat du jour"],
                    ["admin-bar-menu-edit-link", "/admin/carte", "Carte Emporter"],
                    ["admin-bar-portfolio-edit-link", "/admin/portfolio", "Portfolio"],
                    ["admin-bar-site-edit-link", "/admin/site", "Site"],
                    ["admin-bar-fermetures-link", "/admin/fermetures", "Jours fermés"],
                    ["admin-bar-tables-link", "/admin/tables", "Tables"],
                  ].map(([testid, to, label]) => (
                    <Link
                      key={testid}
                      data-testid={testid}
                      to={to}
                      onClick={(e) => {
                        const outer = e.currentTarget.closest(
                          "details[data-testid='admin-bar-menu']"
                        );
                        if (outer) outer.open = false;
                      }}
                      className="block px-4 py-2.5 pl-9 text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#102C1F]/5"
                    >
                      {label}
                    </Link>
                  ))}
                </div>
              </details>
              <button
                data-testid="admin-bar-logout-btn"
                onClick={logout}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#102C1F]/5"
              >
                <LogOut size={11} /> Déconnexion
              </button>
            </div>
          </details>
        </div>
      </div>
    </header>
  );
}
